import { useTheme } from "@emotion/react";
import { Autocomplete, Chip, Grid, IconButton, Paper, Typography, TextField } from "@mui/material";
import React from "react";
import { ReactComponent as CloseIcon } from "../../../assets/close_icon.svg";
import { Primary, Secondary } from '../../../ui-components/Buttons';
import CustomLabel from "ui-components/CustomLabel";
import CustomInputField from "../../../ui-components/CustomInputField";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import { FetchAllStartupPlans } from "api/utils";


const FilterPopup = ({
  tabValue,
  queryParams,
  setQueryParams,
  handleClose,
  handleSubmit,
  handleReset
}) => {
  const theme = useTheme();
  const [filterQueryParams, setFilterQueryParams] = React.useState(queryParams);

  // Plan type options for subscriptions. Derived from the plans config rather
  // than hardcoded: the admin listing filters on plan_details.title, so a plan
  // added or renamed in config would otherwise be unfilterable. Retired plans
  // are kept in the list — their subscribers are exactly who admins look for.
  const { data: startupPlans } = FetchAllStartupPlans();

  const planTypeOptions = React.useMemo(() => {
    const titles = Object.values(startupPlans?.plans || {})
      // Only plans that are actually sold can appear on a subscription.
      .filter((plan) =>
        plan?.title &&
        Object.values(plan?.razorpay_config || {}).some((regionConfig) =>
          Object.values(regionConfig || {}).some((freq) => freq?.plan_id)
        )
      )
      .map((plan) => plan.title);

    const uniqueTitles = Array.from(new Set(titles));

    return uniqueTitles.length
      ? uniqueTitles.map((title) => ({ label: title, value: title }))
      : [
        { label: 'Plus', value: 'Plus' },
        { label: 'Pro', value: 'Pro' },
      ];
  }, [startupPlans]);

  // Status options for subscriptions
  const subscriptionStatusOptions = [{ label: 'Active', value: 'Active' }, { label: 'Expired', value: 'Expired' }, { label: "Cancelled", value: "Cancelled" }];

  // Report type options for one-time purchases
  // const reportTypeOptions = [
  //   { label: 'Fundability Report', value: "fundability_report"},
  //   { label: 'Pitch Deck Audit', value: "pitch_deck_analyser"}
  //   'Pitch Deck Audit',
  //   'Market Analysis',
  //   'Competitive Landscape',
  //   'Financial Projections'
  // ];

  const handleFilterChange = (field, value) => {
    setFilterQueryParams({ ...filterQueryParams, [field]: value });
  };

  const handleFilterSubmit = () => {
    const result = Object.fromEntries(
      Object.entries(filterQueryParams).filter(
        ([_, v]) => v !== undefined && (typeof v === 'string' ? v.trim().length > 0 : v !== null)
      )
    );
    handleSubmit({ query: result });
  };

  const handleFilterReset = () => {
    setFilterQueryParams({});
    handleReset();
  };

  return (
    <Paper
      sx={{
        background: theme.palette.customColors.whiteBG,
        borderRadius: "8px",
      }}
    >
      <IconButton sx={{ position: "absolute", top: 20, right: 20, padding: 0 }}>
        <CloseIcon width={40} height={40} onClick={handleClose} />
      </IconButton>
      <Grid display={"grid"} container padding={"1.5rem"} gap={"1.5rem"}>
        <Grid item>
          <Typography
            sx={{
              fontSize: "1.5rem",
              fontWeight: 700,
              lineHeight: "28px",
              color: theme.palette.customColors.g300,
            }}
          >
            Filter
          </Typography>
        </Grid>

        <Grid xs={12} item container columnSpacing={2} rowSpacing={2} justifyContent={"space-between"}>
          {/* Plan Type Filter - Only for Subscriptions */}
          {tabValue === 'subscriptions' && (
            <Grid item xs={12} sm={6}>
              <CustomLabel>Plan Type</CustomLabel>
              <Autocomplete
                multiple
                disablePortal
                options={Object.keys(filterQueryParams)?.length > 0 && filterQueryParams?.plan_type ? planTypeOptions?.filter(item => !filterQueryParams?.plan_type?.includes(item?.value)) : planTypeOptions}
                fullWidth
                value={Object.keys(filterQueryParams)?.length > 0 && filterQueryParams?.plan_type ? filterQueryParams?.plan_type.split(",").map(type => ({ label: type, value: type })) : []}
                autoHighlight
                onChange={(e, newValue) => {
                  console.log(newValue, "newValue")
                  const selectedPlanTypes = newValue.map(option => option.value).join(",");
                  handleFilterChange('plan_type', selectedPlanTypes);
                }}
                renderTags={(selected, getTagProps) =>
                  selected.map((option, index) => (
                    <Chip key={option.value} label={option.label} {...getTagProps({ index })} />
                  ))
                }
                renderInput={(params) => (
                  <CustomInputField
                    variant="outlined"
                    placeholder="Plan Type"
                    {...params}
                    sx={{ marginBottom: 0 }}
                    className="filter-autocomplete"
                  />
                )}
              />
            </Grid>
          )}

          {/* Status Filter - Subscriptions */}
          {tabValue === 'subscriptions' && (
            <Grid item xs={12} sm={6}>
              <CustomLabel>Plan Status</CustomLabel>
              <Autocomplete
                multiple
                disablePortal
                options={Object.keys(filterQueryParams)?.length > 0 && filterQueryParams?.plan_status ? subscriptionStatusOptions?.filter(item => !filterQueryParams?.plan_status?.includes(item?.value)) : subscriptionStatusOptions}
                fullWidth
                value={Object.keys(filterQueryParams)?.length > 0 && filterQueryParams?.plan_status ? filterQueryParams?.plan_status.split(",").map(type => ({ label: type, value: type })) : []}
                autoHighlight
                onChange={(e, newValue) => {
                  console.log(newValue, "newValue")
                  const selectedPlanStatuses = newValue.map(option => option.value).join(",");
                  handleFilterChange('plan_status', selectedPlanStatuses);
                }}
                renderTags={(selected, getTagProps) =>
                  selected.map((option, index) => (
                    <Chip key={option.value} label={option.label} {...getTagProps({ index })} />
                  ))
                }
                renderInput={(params) => (
                  <CustomInputField
                    variant="outlined"
                    placeholder="Plan Status"
                    {...params}
                    sx={{ marginBottom: 0 }}
                    className="filter-autocomplete"
                  />
                )}
              />
            </Grid>
          )}

          {/* Report Type Filter - Only for One-Time Purchases */}
          {/* Report Type Filter - Only for One-Time Purchases (kept commented out)
          {tabValue === 'one_time' && (
            <Grid item xs={12} sm={6}>
              <CustomLabel>Report Type</CustomLabel>
              ...
            </Grid>
          )} */}

          {/* Date Range Filter - Start Date */}
          <Grid item xs={12} sm={6}>
            <CustomLabel>From Date</CustomLabel>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                value={filterQueryParams?.from_date ? moment(filterQueryParams.from_date) : null}
                maxDate={filterQueryParams?.to_date ? moment(filterQueryParams.to_date) : undefined}
                onChange={(newValue) => {
                  handleFilterChange('from_date', newValue?.toISOString());
                }}
                renderInput={(params) => <TextField {...params} fullWidth />}
                sx={{
                  width: "100%",
                  "& > .MuiInputBase-root.MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    "& > fieldset.MuiOutlinedInput-notchedOutline": {
                      borderColor: "#ebe8fb !important",
                    }
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>

          {/* Date Range Filter - End Date */}
          <Grid item xs={12} sm={6}>
            <CustomLabel>To Date</CustomLabel>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                value={filterQueryParams?.to_date ? moment(filterQueryParams.to_date) : null}
                minDate={filterQueryParams?.from_date ? moment(filterQueryParams.from_date) : undefined}
                onChange={(newValue) => {
                  handleFilterChange('to_date', newValue?.toISOString());
                }}
                renderInput={(params) => <TextField {...params} fullWidth />}
                sx={{
                  width: "100%",
                  "& > .MuiInputBase-root.MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    "& > fieldset.MuiOutlinedInput-notchedOutline": {
                      borderColor: "#ebe8fb !important",
                    }
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>

        </Grid>

        {/* Action Buttons */}
        <Grid item container xs={12} gap={2} display={"flex"} flexWrap={"nowrap"} justifyContent={"center"}>
          <Secondary
            onClick={handleFilterReset}
            sx={{
              padding: "0.75rem 1.25rem",
              fontSize: "1rem",
              minWidth: 120
            }}
          >
            Reset
          </Secondary>
          <Primary
            onClick={handleFilterSubmit}
            sx={{
              padding: "0.75rem 1.25rem",
              fontSize: "1rem",
              minWidth: 120
            }}
          >
            Filter
          </Primary>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default FilterPopup;
